import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AccountsTable } from "@/components/accounts/accounts-table";
import { AppLink } from "@/components/common/app-link";
import { Page, PageTitle } from "@/components/common/page";
import { PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { client } from "@/data";
import { qForProject } from "@/data/queries";
import { projectPath } from "@/lib/nav";

export const Route = createFileRoute("/p/$project/accounts/")({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        qForProject(params.project).accounts({ limit: 200 }),
      ),
      ...(client.kind === "fixtures"
        ? [context.queryClient.ensureQueryData(qForProject(params.project).endUsers())]
        : []),
    ]),
  pendingComponent: PageSkeleton,
  component: AccountsPage,
});

function AccountsPage() {
  const q = qForProject(Route.useParams().project);
  const { project } = Route.useParams();
  const { data: accounts } = useSuspenseQuery(q.accounts({ limit: 200 }));

  return (
    <Page wide>
      <PageTitle
        title="Accounts"
        description="Every mailbox, calendar and address book your users have linked, with what each one has left against the provider's own quotas."
        actions={
          <Button size="sm" asChild>
            <AppLink
              to={
                client.kind === "http" ? "/signed-in" : projectPath(project, "/connect")
              }
            >
              {client.kind === "http" ? "Account setup" : "Connect an account"}
            </AppLink>
          </Button>
        }
      />
      {/* The filters live in the query string, so a support link can open
          exactly the subset a customer is asking about. */}
      {accounts.truncated ? (
        <p role="status">
          Showing at most 200 accounts. More may exist; the API does not support
          pagination yet.
        </p>
      ) : null}
      {client.kind === "http" ? (
        <p className="text-sm text-muted-foreground">
          End users and account statistics are not available yet. Missing fields are
          shown as Unknown.
        </p>
      ) : null}
      {client.kind === "fixtures" ? (
        <FixtureAccounts accounts={accounts.items} project={project} />
      ) : (
        <AccountsTable accounts={accounts.items} endUsers={[]} project={project} />
      )}
    </Page>
  );
}

function FixtureAccounts({
  accounts,
  project,
}: {
  accounts: import("@/data/types").ConnectedAccount[];
  project: string;
}) {
  const { data: endUsers } = useSuspenseQuery(qForProject(project).endUsers());
  return (
    <AccountsTable accounts={accounts} endUsers={endUsers.items} project={project} />
  );
}
